/**
 * Camada 1c · o sino do PLANO DE CONTROLE.
 *
 * DE ONDE ELE VEIO: `apps/admin/src/dados/usar-sino-admin.ts`, 80 linhas que viviam dentro do
 * app. Elas existiam por um motivo real, e o comentário de lá já dizia: o `usarSino` do
 * `packages/chat` exige tenant, e o operador do plano de controle não tem nenhum.
 *
 * POR QUE ARQUIVO PRÓPRIO, e não um `if` dentro de `transporte-de-mensagens.ts`: ele fala com
 * OUTRO serviço (`control-plane/admin-api`), por outro caminho (`/mensagens`), com outro
 * guarda (`X-Operador-Email` em vez de `X-Tenant-Id`). Misturá-lo no anterior faria crescer
 * ali dentro um `if` por produto, que é exatamente o que este pacote existe para impedir.
 *
 * A DIFERENÇA QUE NÃO SOME, e ela fica declarada: no plano de controle não há cliente. Mandar
 * um `X-Tenant-Id` de fachada faz o `admin-api` recusar com 401, e o sino fica indisponível
 * para sempre sem ninguém entender por quê.
 */
import { useEffect, useState } from 'react';
import type { EstadoSino } from '@complexity/chat';
import type { Credenciais } from './credenciais.js';

/** De quanto em quanto tempo o sino relê. Dois segundos é o que o admin já usava. */
const INTERVALO_MS = 2_000;

/** A linha que a rota `/mensagens` do admin-api devolve, no que interessa ao sino. */
interface LinhaDeConversa {
  readonly nao_lidas: number;
}

/**
 * Conta as não lidas do operador, relendo a cada dois segundos.
 *
 * BASE `undefined` DESLIGA O GANCHO SEM CHAMAR NADA, e isto é o que permite os dois sinos
 * serem chamados sempre, como a regra dos hooks exige: o que não é o escolhido recebe
 * `undefined` e sai do efeito na primeira linha.
 *
 * NASCE INDISPONÍVEL E NÃO ZERO, e a decisão é a mesma do `usarSino`: antes da primeira
 * leitura, "não sei" e "não há mensagem" são coisas diferentes, e mostrar zero afirma a
 * segunda quando a verdade é a primeira.
 */
export function usarSinoDoControlPlane(
  base: string | undefined,
  credenciais: Credenciais,
): EstadoSino {
  const [estado, definirEstado] = useState<EstadoSino>({ naoLidos: 0, indisponivel: true, medidoEm: null });

  useEffect(() => {
    // Sem base, este não é o plano deste app. Sair aqui é o que evita chamada inútil.
    if (base === undefined) return undefined;

    const raiz = base.replace(/\/+$/, '');
    // `vivo` protege contra a resposta que chega DEPOIS de o componente sair da tela. Sem ele,
    // o React avisa sobre estado em componente desmontado, e o aviso esconde o vazamento do
    // intervalo atrás de uma linha de console.
    let vivo = true;

    const medir = async (): Promise<void> => {
      try {
        const { token, email } = credenciais.obter();
        const resposta = await fetch(`${raiz}/mensagens`, {
          headers: {
            Accept: 'application/json',
            // O guarda do admin-api lê o e-mail do operador do token já verificado. O
            // cabeçalho de tenant NÃO entra: é a diferença inteira deste plano.
            ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
            // O CABECALHO QUE O GUARDA LE. operador.guard.ts confere este e-mail contra
            // app.operadores_control_plane a CADA requisicao, e sem ele a resposta e 401.
            // A primeira versao deste arquivo o esqueceu, e o sino do admin ficaria
            // indisponivel para sempre: achado pela comparacao com as 86 linhas de
            // usar-sino-admin.ts, que mandavam o cabecalho. Medido em 05/09/2026.
            ...(email === null ? {} : { 'X-Operador-Email': email }),
          },
        });
        if (!resposta.ok) throw new Error(String(resposta.status));

        const linhas = (await resposta.json()) as LinhaDeConversa[];
        if (!vivo) return;

        // A SOMA É DAS NÃO LIDAS DE CADA CONVERSA, e não a contagem de conversas: duas
        // conversas com três não lidas cada são seis, e não duas. O sino conta o que a pessoa
        // ainda precisa ler.
        definirEstado({
          naoLidos: linhas.reduce((soma, l) => soma + (l.nao_lidas ?? 0), 0),
          indisponivel: false,
          // A HORA DA MEDICAO faz parte do contrato de EstadoSino, e nao e enfeite: e ela que
          // deixa a tela distinguir "zero agora" de "zero de cinco minutos atras".
          medidoEm: new Date(),
        });
      } catch {
        // Falha vira INDISPONÍVEL, e nunca zero. A diferença aparece na tela: o sino tem marca
        // própria para "não sei", e ela é o que impede a pessoa de concluir que não há
        // mensagem quando o que houve foi a fonte cair.
        if (vivo) definirEstado((antes) => ({ naoLidos: 0, indisponivel: true, medidoEm: antes.medidoEm }));
      }
    };

    void medir();
    const relogio = setInterval(() => void medir(), INTERVALO_MS);
    return () => {
      vivo = false;
      clearInterval(relogio);
    };
  }, [base, credenciais]);

  return estado;
}
